import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';

import { cn } from 'lib/utils';
import { MODAL_OVERLAY_CLASS, MODAL_SIDE_PANEL_SURFACE_CLASS } from './modal-styles';

const Sheet = DialogPrimitive.Root;
const SheetTrigger = DialogPrimitive.Trigger;
const SheetClose = DialogPrimitive.Close;
const SheetPortal = DialogPrimitive.Portal;
const SheetTitle = DialogPrimitive.Title;
const SheetDescription = DialogPrimitive.Description;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(MODAL_OVERLAY_CLASS, 'z-[70]', className)}
    data-testid="sheet-overlay"
    {...props}
  />
));
SheetOverlay.displayName = DialogPrimitive.Overlay.displayName;

interface SheetContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  overlayClassName?: string;
  side?: 'right' | 'bottom';
}

const SHEET_POSITION_CLASS: Record<NonNullable<SheetContentProps['side']>, string> = {
  right:
    'fixed inset-y-0 right-0 h-full w-full max-w-xs flex-col p-6 duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
  bottom:
    'fixed inset-x-0 bottom-0 max-h-[85dvh] w-full flex-col rounded-t-[24px] p-6 duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
};

const SheetContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>((
  {
    className,
    children,
    overlayClassName,
    side = 'right',
    onOpenAutoFocus,
    onCloseAutoFocus,
    ...props
  },
  ref,
) => {
  const returnFocusTargetRef = React.useRef<HTMLElement | null>(null);

  const handleOpenAutoFocus = (event: Event) => {
    const activeElement = document.activeElement;
    returnFocusTargetRef.current =
      activeElement instanceof HTMLElement && activeElement !== document.body
        ? activeElement
        : null;
    onOpenAutoFocus?.(event);
  };

  const handleCloseAutoFocus = (event: Event) => {
    onCloseAutoFocus?.(event);
    if (event.defaultPrevented) return;

    const returnFocusTarget = returnFocusTargetRef.current;
    if (!returnFocusTarget) return;

    window.setTimeout(() => {
      if (returnFocusTarget.isConnected) {
        returnFocusTarget.focus({ preventScroll: true });
      }
    }, 0);
  };

  return (
    <SheetPortal>
      <SheetOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'z-[70] flex gap-4 overflow-y-auto',
          MODAL_SIDE_PANEL_SURFACE_CLASS,
          SHEET_POSITION_CLASS[side],
          className
        )}
        onOpenAutoFocus={handleOpenAutoFocus}
        onCloseAutoFocus={handleCloseAutoFocus}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </SheetPortal>
  );
});
SheetContent.displayName = DialogPrimitive.Content.displayName;

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetDescription,
};
